import { useMemo, useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import AppLayout from '~/components/appLayout'
import RecordsMenu from '~/components/recordsMenu'
import { toastError, toastSuccess } from '~/components/toasts'
import useAction from '~/prix/react/hooks/action'
import useAPI from '~/prix/react/hooks/api'
import { institutionalColors, primaryColors } from '~/design'
import insightListDefinition from './list/insightList.definition'
import {
  ALERT_BADGES_TYPES,
  DEFAULT_ALERT_PRESET_FILTER,
  getAllAlertTypeOptions,
} from './alertTypes.constants'
import SelectStaticAutocomplete from '~/prix/react/components/form/inputs/selectStaticAutocomplete'
import { ITEMS_PER_PAGE, LOAD_BATCH_SIZE } from './insight.constants'
import { ListWithAutoLoad } from './insightList.components'
import { getAlertLabel, getAlertColor, getAlertTextColor } from '~/packages/general/variable/spaceTime.data'
import { Insight } from '~/packages/general/variable/variables.typing'
import { ExportInsightModal } from './export/buildInsightsExportModal'
import useInsightExport, { InsightExportFilters } from '~/prix/react/hooks/useInsightExport'

function isAbortError(error: unknown) {
  return typeof error === 'object' && error !== null && (error as any).key === 'abort'
}

const Container = styled.div`
  padding: 32px;
  max-width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    padding: 16px;
  }
`

const PageTitle = styled.h1`
  color: ${primaryColors.action};
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 24px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    font-size: 1.5rem;
  }
`

const FiltersContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  grid-auto-flow: dense;
  gap: 24px;
  margin-bottom: 16px;

  @media (max-width: 1599px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 20px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const FilterLabel = styled.label`
  color: ${institutionalColors.neutral30};
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const Input = styled.input`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.9375rem;
  color: ${institutionalColors.neutral30};
  transition: all 0.2s ease;
  height: 46px;

  &::placeholder {
    color: ${institutionalColors.neutral60};
  }

  &:hover {
    border-color: ${primaryColors.action};
  }

  &:focus {
    outline: none;
    border-color: ${primaryColors.action};
    box-shadow: 0 0 0 3px rgba(0, 94, 184, 0.1);
  }

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 12px;
  }
`

const FilterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    flex-direction: column;

    button {
      width: 100%;
    }
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mobile + 1}px) and (max-width: 1024px) {
    /* Em tablets, botões se ajustam proporcionalmente */
    button {
      flex: 1;
      min-width: 140px;
    }
  }
`

const ClearButton = styled.button<{ $active: boolean }>`
  padding: 6px 20px;
  border-radius: 8px;
  border: none;
  background: ${({ $active }) => ($active ? '#dc3545' : '#e0e0e0')};
  color: ${({ $active }) => ($active ? 'white' : '#999')};
  font-weight: 600;
  font-size: 0.875rem;
  cursor: ${({ $active }) => ($active ? 'pointer' : 'not-allowed')};
  transition: all 0.2s ease;

  &:hover {
    ${({ $active }) => $active && 'background: #c82333; transform: translateY(-1px);'}
    ${({ $active }) => $active && 'box-shadow: 0 4px 8px rgba(220, 53, 69, 0.2);'}
  }

  &:active {
    ${({ $active }) => $active && 'transform: translateY(0);'}
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    padding: 12px 16px;
  }

  @media (max-width: 768px) {
    padding: 0 16px;
    min-height: 40px;
  }
`

const SearchContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: stretch;
`

const SearchButton = styled.button<{ $disabled: boolean }>`
  padding: 6px 20px;
  border-radius: 8px;
  border: none;
  background: ${({ $disabled }) => ($disabled ? '#e0e0e0' : primaryColors.action)};
  color: ${({ $disabled }) => ($disabled ? '#999' : 'white')};
  font-weight: 600;
  font-size: 0.875rem;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;
  white-space: nowrap;
  min-width: 118px;

  &:hover {
    ${({ $disabled }) => !$disabled && `opacity: 0.9; transform: translateY(-1px);`}
    ${({ $disabled }) => !$disabled && `box-shadow: 0 4px 8px rgba(0, 86, 179, 0.2);`}
  }

  &:active {
    ${({ $disabled }) => !$disabled && 'transform: translateY(0);'}
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    min-width: unset;
    padding: 12px 16px;
  }

  @media (max-width: 768px) {
    padding: 10px 16px;
    min-height: 40px;
  }
`

const ErrorContainer = styled.div`
  background: #fee;
  border-left: 4px solid #dc3545;
  padding: 16px 20px;
  border-radius: 8px;
  margin-bottom: 20px;

  strong {
    color: #dc3545;
    display: block;
    margin-bottom: 8px;
  }

  p {
    color: #721c24;
    margin: 0;
  }
`

const ContentCard = styled.div`
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0px 10px 20px rgba(20, 46, 82, 0.15);
  overflow: hidden;
`

const CardSection = styled.div<{ $noBorder?: boolean }>`
  padding: 20px 24px;
  border-bottom: ${({ $noBorder }) => ($noBorder ? 'none' : '1px solid #e6e6e6')};

  @media (max-width: 768px) {
    padding: 16px 20px;
  }
`
const ExportButton = styled.button<{ $disabled: boolean }>`
  padding: 6px 20px;
  border-radius: 8px;
  border: 2px solid ${({ $disabled }) => ($disabled ? '#e0e0e0' : primaryColors.action)};
  background: white;
  color: ${({ $disabled }) => ($disabled ? '#999' : primaryColors.action)};
  font-weight: 600;
  font-size: 0.875rem;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;
  white-space: nowrap;
  min-width: 118px;
  min-height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    ${({ $disabled }) =>
      !$disabled &&
      `
      background: ${primaryColors.action};
      color: white;
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(0, 86, 179, 0.2);
    `}
  }

  &:active {
    ${({ $disabled }) => !$disabled && 'transform: translateY(0);'}
  }

  svg {
    width: 16px;
    height: 16px;
  }

  @media (max-width: 768px) {
    padding: 10px 16px;
    min-height: 40px;
  }
`

export interface FormattedInsight {
  id: string
  description: string
  alertBadge: {
    label: string
    color: string
    textColor: string
  } | null
  variable: {
    title: string | null
    label: string | null
  }
  context: {
    delimitation: string | null
    updatedAt: string | null
  }
  alertType: string
  variableKey: string | null
  conclusionSpace: any
  insight?: Insight
}

export default function InsightListScreen() {
  const { context } = useAPI()
  const [loadedInsights, setLoadedInsights] = useState<any[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [loadTrigger, setLoadTrigger] = useState(0)
  const [sortKey, setSortKey] = useState<InsightExportFilters['sortBy']>(undefined)
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [isContextReady, setIsContextReady] = useState(false)
  const [showExportModal, setShowExportModal] = useState(false)

  const { exportInsights, isLoading: isExporting } = useInsightExport()

  const [filters, setFilters] = useState<{
    alertType: string[]
    alertPreset: string
    search: string
  }>({
    alertType: [],
    alertPreset: DEFAULT_ALERT_PRESET_FILTER,
    search: '',
  })

  const [currentPageKey, setCurrentPageKey] = useState(0)

  const lastAppliedFiltersRef = useRef(filters)

  // Verificar se o contexto está pronto (usuário autenticado)
  useEffect(() => {
    if (context.user && context.user.id) {
      setIsContextReady(true)
    }
  }, [context.user])

  const handleSearch = () => {
    lastAppliedFiltersRef.current = { ...filters }
    // Reset antes de disparar nova busca
    setLoadedInsights([])
    setTotalCount(0)
    setCurrentPageKey(prev => prev + 1)
    setLoadTrigger(prev => prev + 1)
  }

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const appliedFilters = lastAppliedFiltersRef.current

  const buildExportFilters = () => ({
    search: lastAppliedFiltersRef.current.search.trim() || undefined,
    alertType:
      lastAppliedFiltersRef.current.alertType.length > 0
        ? lastAppliedFiltersRef.current.alertType
        : undefined,
    alertPreset:
      lastAppliedFiltersRef.current.alertPreset !== 'all'
        ? lastAppliedFiltersRef.current.alertPreset
        : undefined,
    sortBy: sortKey || undefined,
    sortOrder,
  })

  const handleExport = async (format: 'xlsx' | 'csv') => {
    const filters = buildExportFilters()

    try {
      const { blob, filename } = await exportInsights(filters, format)

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      toastSuccess('Exportação concluída com sucesso!')
      setShowExportModal(false)
    } catch (error) {
      console.error('[InsightListScreen] Export error', error)
      toastError('Não foi possível exportar os insights. Tente novamente.')
    }
  }

  // Calcular índices para próximo carregamento
  // Sempre baseado no que já foi carregado após reset
  const startIndex = loadedInsights.length
  const endIndex = startIndex + LOAD_BATCH_SIZE

  const insightsResult = useAction(
    insightListDefinition,
    {
      startIndex,
      endIndex,
      search: appliedFilters.search.trim() || undefined,
      alertType: appliedFilters.alertType.length > 0 ? appliedFilters.alertType : undefined,
      alertPreset:
        appliedFilters.alertPreset !== 'all' ? appliedFilters.alertPreset : undefined,
      sortBy: sortKey || undefined,
      sortOrder: sortOrder,
    },
    [loadTrigger, sortKey, sortOrder],
    {
      autoLoad: isContextReady,
    },
  )

  const newItems = insightsResult.result?.items || []
  const totalFromBackend = insightsResult.result?.total || 0

  // Atualizar dados carregados quando recebemos novos do backend
  useEffect(() => {
    if (newItems.length > 0 && !insightsResult.isLoading) {
      setLoadedInsights(prev => {
        // Se é primeira carga ou após reset de filtros
        if (prev.length === 0) {
          setTotalCount(totalFromBackend)
          return newItems
        }

        const existingIds = new Set(prev.map((item: any) => item.id))
        const uniqueNewItems = newItems.filter((item: any) => !existingIds.has(item.id))

        if (uniqueNewItems.length > 0) {
          return [...prev, ...uniqueNewItems]
        }

        return prev
      })

      setTotalCount(totalFromBackend)
    }
  }, [newItems, totalFromBackend, insightsResult.isLoading])

  const formattedInsights = useMemo((): FormattedInsight[] => {
    return loadedInsights.map(insight => {
      return {
        id: insight.id,
        description: insight.description,
        alertBadge: insight.alert
          ? {
              label: getAlertLabel(insight.alert),
              color: getAlertColor(insight.alert),
              textColor: getAlertTextColor(insight.alert),
            }
          : null,
        variable: {
          title: insight.variableTitle,
          label: insight.variableLabel ?? null,
        },
        context: {
          delimitation: insight.conclusionSpace?.delimitationTitle ?? null,
          updatedAt: insight.updatedAt ?? null,
        },
        alertType: insight.title,
        variableKey: insight.variableKey,
        conclusionSpace: insight.conclusionSpace,
        insight: insight,
      }
    })
  }, [loadedInsights])

  // Insight detalhado
  // const [selectedInsightId, setSelectedInsightId] = useState<string | null>(null)
  // const insightDetailResult = useAction(
  //   insightReadDefinition,
  //   selectedInsightId ? { id: selectedInsightId } : undefined,
  //   [selectedInsightId],
  // )

  // const toggleExpanded = useCallback((id: string) => {
  //   setSelectedInsightId(prev => (prev === id ? null : id))
  // }, [])

  // const selectedInsight = (insightDetailResult.result as any)?.insight || null

  // ESC key handler to collapse expanded insight
  // useEffect(() => {
  //   const handleEscape = (e: KeyboardEvent) => {
  //     if (e.key === 'Escape' && selectedInsightId) {
  //       setSelectedInsightId(null)
  //     }
  //   }
  //   window.addEventListener('keydown', handleEscape)
  //   return () => window.removeEventListener('keydown', handleEscape)
  // }, [selectedInsightId])

  const clearFilters = () => {
    const defaultFilters = {
      alertType: [],
      alertPreset: DEFAULT_ALERT_PRESET_FILTER,
      search: '',
    }

    setFilters(defaultFilters)
    lastAppliedFiltersRef.current = defaultFilters
    setLoadedInsights([])
    setTotalCount(0)
    setCurrentPageKey(prev => prev + 1)
    setLoadTrigger(prev => prev + 1)
  }
  const activeFiltersCount =
    (Array.isArray(filters.alertType) && filters.alertType.length > 0 ? 1 : 0) +
    (filters.alertPreset !== DEFAULT_ALERT_PRESET_FILTER ? 1 : 0) +
    (filters.search.trim() ? 1 : 0)

  const exportFilters = buildExportFilters()
  const visibleError = insightsResult.error && !isAbortError(insightsResult.error)

  return (
    <>
      <AppLayout
        title='Insights'
        mustBeAuthenticated={true}
        menuDeskWidth={320}
        onlyChildren={true}
        menu={<RecordsMenu menuActive='insights' />}
      >
        <Container>
          <PageTitle>Lista de Insights</PageTitle>

          {/* Card de Filtros */}
          <ContentCard style={{ marginBottom: '24px' }}>
            <CardSection $noBorder>
              <FiltersContainer>
                <FiltersGrid>
                  {/* Busca por Nome */}
                  <FilterGroup>
                    <FilterLabel>Busca geral</FilterLabel>
                    <SearchContainer>
                      <Input
                        type='text'
                        value={filters.search}
                        onChange={e => setFilters({ ...filters, search: e.target.value })}
                        onKeyPress={handleSearchKeyPress}
                        placeholder='Ex: setembro redução atendimento'
                      />
                    </SearchContainer>
                  </FilterGroup>

                  {/* Tipo de alerta */}
                  <FilterGroup>
                    <FilterLabel>Tipo de alerta</FilterLabel>
                    <SelectStaticAutocomplete
                      options={getAllAlertTypeOptions()}
                      value={filters.alertType}
                      onChange={(value: any) => setFilters({ ...filters, alertType: value })}
                      placeholder='Selecionar tipo de alerta...'
                      isClearable={false}
                      isMultiple={true}
                    />
                  </FilterGroup>

                  {/* Alerta */}
                  <FilterGroup>
                    <FilterLabel>Alerta</FilterLabel>
                    <SelectStaticAutocomplete
                      options={Object.entries(ALERT_BADGES_TYPES).map(([key, label]) => ({
                        value: key,
                        label: label,
                      }))}
                      value={filters.alertPreset}
                      onChange={(value: any) =>
                        setFilters({ ...filters, alertPreset: value as string })
                      }
                      placeholder='Selecionar alerta...'
                      isClearable={false}
                    />
                  </FilterGroup>
                </FiltersGrid>

                <FilterActions>
                  <ExportButton
                    onClick={() => setShowExportModal(true)}
                    $disabled={insightsResult.isLoading || totalCount === 0}
                    disabled={insightsResult.isLoading || totalCount === 0}
                    title='Exportar insights'
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
                      />
                    </svg>
                    Exportar
                  </ExportButton>
                  <ClearButton
                    onClick={clearFilters}
                    disabled={activeFiltersCount === 0}
                    $active={activeFiltersCount > 0}
                  >
                    Limpar Filtros {activeFiltersCount > 0 && `(${activeFiltersCount})`}
                  </ClearButton>
                  <SearchButton
                    onClick={handleSearch}
                    $disabled={insightsResult.isLoading}
                    disabled={insightsResult.isLoading}
                  >
                    Buscar
                  </SearchButton>
                </FilterActions>
              </FiltersContainer>
            </CardSection>
          </ContentCard>

          {visibleError && (
            <ErrorContainer>
              <strong>Erro ao carregar insights</strong>
              <p>{String(insightsResult.error)}</p>
            </ErrorContainer>
          )}

          {!visibleError && (
            <ContentCard>
              {/* Seção da Lista */}
              <CardSection $noBorder>
                <ListWithAutoLoad
                  key={currentPageKey}
                  formattedInsights={formattedInsights}
                  totalCount={totalCount}
                  loadedCount={loadedInsights.length}
                  isLoading={insightsResult.isLoading}
                  onPageChange={(page: number) => {
                    // Verificar se precisamos carregar mais dados
                    const neededItems = page * ITEMS_PER_PAGE
                    if (neededItems > loadedInsights.length && loadedInsights.length < totalCount) {
                      setLoadTrigger(prev => prev + 1)
                    }
                  }}
                  onSortChange={(key: string, order: 'asc' | 'desc') => {
                    setSortKey((key || undefined) as InsightExportFilters['sortBy'])
                    setSortOrder(order)
                    // reset paging on sort change
                    setLoadedInsights([])
                    setTotalCount(0)
                    setCurrentPageKey(prev => prev + 1)
                  }}
                  currentSort={sortKey ? { key: sortKey, order: sortOrder } : undefined}
                />
              </CardSection>
            </ContentCard>
          )}
        </Container>
      </AppLayout>
      <ExportInsightModal
        isOpen={showExportModal}
        totalCount={totalCount}
        exportFilters={exportFilters}
        onClose={() => !isExporting && setShowExportModal(false)}
        onExport={handleExport}
        isExporting={isExporting}
      />
    </>
  )
}
